import { fetchAPI } from "./utils";

export interface S3UploadParams {
    file: File;
    bucket?: string;
    isPublic?: boolean;
    isFolder?: boolean;
    /** Optional file size in bytes; when set for video uploads, backend enforces waypoint video limit (20 MB) */
    fileSize?: number;
}

export interface S3UploadResponse {
    success: boolean;
    uploadUrl: string;
    finalUrl: string;
    key: string;
    bucket: string;
    temporaryDownloadUrl?: string;
    isPublic: boolean;
}

export interface S3DownloadParams {
    fileUrl: string;
    mode?: "attachment" | "inline";
}

export interface S3DownloadResponse {
    signedUrl: string;
}

/**
 * Get a presigned URL for uploading a file to S3
 * @param params - Upload parameters including file, bucket, and privacy settings
 * @param token - Authentication token
 * @returns Promise with upload response containing presigned URL and final URL
 */
export const getPresignedUploadUrl = async (params: S3UploadParams, token: string): Promise<S3UploadResponse> => {
    const { file, bucket = "rewindstories", isPublic = true, isFolder = false, fileSize } = params;

    const body: Record<string, unknown> = {
        filename: file.name,
        fileType: file.type,
        bucket,
        isPublic,
        isFolder,
    };
    if (fileSize != null) {
        body.fileSize = fileSize;
    }

    const response = await fetchAPI<S3UploadResponse>("/get-presigned-url", "POST", body, token);

    return response;
};

/**
 * Get a signed download URL for a private S3 file
 * @param params - Download parameters including file URL and mode
 * @param token - Authentication token
 * @returns Promise with signed download URL
 */
export const getSignedDownloadUrl = async (params: S3DownloadParams, token: string): Promise<S3DownloadResponse> => {
    const { fileUrl, mode = "attachment" } = params;

    const response = await fetchAPI<S3DownloadResponse>(
        "/get-signed-download-url",
        "POST",
        {
            fileUrl,
            mode,
        },
        token,
    );

    return response;
};

/**
 * Upload a file to S3 using presigned URL (low-level function)
 * @param file - File to upload
 * @param uploadUrl - Presigned upload URL
 * @returns Promise that resolves when upload is complete
 */
const uploadFileToS3WithUrl = async (file: File, uploadUrl: string): Promise<void> => {
    const response = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
            "Content-Type": file.type,
        },
    });

    if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
    }
};

/**
 * Complete S3 upload workflow: get presigned URL and upload file
 * @param params - Upload parameters
 * @param token - Authentication token
 * @returns Promise with final URL of uploaded file
 */
export const uploadFileToS3Complete = async (params: S3UploadParams, token: string): Promise<string> => {
    // Get presigned URL
    const uploadResponse = await getPresignedUploadUrl(params, token);

    // Upload file using presigned URL
    await uploadFileToS3WithUrl(params.file, uploadResponse.uploadUrl);

    // Return the final URL
    return uploadResponse.finalUrl;
};

/**
 * Upload file to S3 - convenience function that matches the expected API signature
 * @param params - Object with token and file
 * @returns Promise with response containing url
 */
export const uploadFileToS3 = async (params: { token: string; file: File }): Promise<{ url: string }> => {
    const url = await uploadFileToS3Complete({ file: params.file }, params.token);
    return { url };
};
